import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import UserData from "../../../services/UserData";
import IngestedDocumentsService from "../../../services/IngestedDocumentsService";
import NotFound from "../../NotFound/NotFound";
import layout from "../IngestionBatches/IngestionBatches.module.css";
import style from "./IngestedDocuments.module.css";

const StructuredValue = ({ value }) => {
  if (value === null || value === undefined) return <span>—</span>;
  if (typeof value !== "object") return <span className={style.text}>{String(value)}</span>;
  const entries = Object.entries(value);
  if (!entries.length) return <span>{Array.isArray(value) ? "[]" : "{}"}</span>;
  return <dl className={style.metadata}>{entries.map(([key, item]) => (
    <div key={key}><dt>{key.replace(/_/g, " ")}</dt><dd><StructuredValue value={item} /></dd></div>
  ))}</dl>;
};

const ImagePreview = ({ data }) => {
  const [failed, setFailed] = useState(false);
  const url = [data?.image_url, data?.url, data?.src].find(value =>
    typeof value === "string" && (/^https?:\/\//i.test(value) || /^data:image\/(png|jpeg|gif|webp);base64,/i.test(value))
  );
  if (!url || failed) return <p>Preview unavailable. Image metadata is shown below.</p>;
  return <img className={style.preview} src={url} alt="Extracted document content" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
};

const ContentUnit = ({ unit, index }) => {
  if (!unit || typeof unit !== "object" || Array.isArray(unit)) return <StructuredValue value={unit} />;
  const rows = unit.data?.rows;
  const isTable = Array.isArray(rows) && rows.every(Array.isArray);
  return (
    <article className={style.unit}>
      <h3>{typeof unit.content_type === "string" ? unit.content_type : "Content"} <small>{index + 1}</small></h3>
      {isTable ? <div className={style.tableScroll}><table className={style.table}><tbody>
        {rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}><StructuredValue value={cell} /></td>)}</tr>)}
      </tbody></table></div> : unit.text != null && <div className={style.text}><StructuredValue value={unit.text} /></div>}
      {(unit.content_type === "IMAGE" || unit.data?.mime_type?.startsWith?.("image/")) && <ImagePreview data={unit.data} />}
      <details className={style.details}><summary>Metadata and details</summary>
        <StructuredValue value={Object.fromEntries(Object.entries(unit).filter(([key]) => key !== "text"))} />
      </details>
    </article>
  );
};

const DocumentContent = () => {
  const { sourceDocumentId } = useParams();
  const location = useLocation();
  const userData = useContext(UserData);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    if (!userData) return () => { active = false; };
    IngestedDocumentsService.getContent(userData, sourceDocumentId, 1)
      .then(async data => {
        if (!active) return;
        if (Array.isArray(data?.content) && Number.isInteger(data.total_pages) && data.total_pages > 1) {
          const content = [...data.content];
          for (let nextPage = 2; nextPage <= data.total_pages; nextPage++) {
            if (!active) return;
            const next = await IngestedDocumentsService.getContent(userData, sourceDocumentId, nextPage);
            if (!Array.isArray(next?.content)) throw new Error("Invalid content response");
            content.push(...next.content);
          }
          if (active) setResponse({ ...data, content });
        } else if (active) setResponse(data);
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userData, sourceDocumentId]);

  const units = Array.isArray(response?.content) ? response.content : Array.isArray(response) ? response : null;
  return (
    <section className={layout.container} aria-label="Document content" aria-busy={loading}>
      <div className={style.contentHeader}>
        <Link className="btn btn-outline-secondary" to="/intelligence/documents" state={{ page: location.state?.page || 1, pageSize: location.state?.pageSize || 10 }}>
          <ArrowLeft size={20} aria-hidden="true" /> Back to ingested documents
        </Link>
        <h2>{location.state?.filename || "Document content"}</h2>
      </div>
      <div className={layout.loadingSlot}>{loading && <div className={layout.loadingBar} role="progressbar" aria-label="Loading document content" />}</div>
      <div className={layout.content}>
        {error ? <NotFound /> : !loading && (units ? (
          units.length ? units.map((unit, index) => <ContentUnit key={unit?.content_unit_id || index} unit={unit} index={index} />) : <p>No document content found.</p>
        ) : response == null ? <p>No document content found.</p> : <ContentUnit unit={response} index={0} />)}
      </div>
    </section>
  );
};

export default DocumentContent;
